import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { unitLabel } from '@d-orca/shared/client/logistics';
import type { IDeliveryLine, IDeliveryLineSource, IDeliverySourceBooked, IDeliverySourceTask } from '@interfaces/ILogistics';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { GanttIcon } from '@utils/icons';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { schedulerTaskPath } from '@constants/schedulerParams';
import { TaskStatusPill } from '../../SchedulerPage/components/TaskStatusPill/TaskStatusPill';
import { formatDay } from '../logistics.format';
import { bookedSummary } from './bookedSummary';
import styles from './DeliverySource.module.css';

/** A source task's name as it reads now, or "Task removed" once it is gone. */
function sourceName(source: IDeliveryLineSource, t: TFunction): string {
  return source.task?.name ?? t('logistics.source.removed');
}

interface IDeliverySourcePillProps {
  sources: IDeliveryLineSource[];
  /** The truck's lines, when it carries several materials: each tooltip row
   *  then names the material its task booked ("Rebar D16 · Formwork 3F"), since
   *  a bare task name would not say which of the three it is on. */
  lines?: IDeliveryLine[];
  /** Spell the count out ("2 tasks") beside the icon — the drawer's material
   *  rows have the room the board card lacks. */
  withCount?: boolean;
}

/**
 * The board card's mark for an order raised from the Scheduler: an icon-only
 * pill on the meta line. The task names live in its tooltip and, for screen
 * readers, in its accessible name — the card has no room to spell them out.
 */
export function DeliverySourcePill({ sources, lines, withCount = false }: IDeliverySourcePillProps) {
  const { t } = useTranslation();
  const title = t('logistics.source.pillTitle', { count: sources.length });
  // The separator is structural punctuation, not copy — the card's meta line
  // uses the same literal " · ".
  const names = lines
    ? lines.flatMap((line) => line.sources.map((s) => `${line.itemName} · ${sourceName(s, t)}`))
    : sources.map((source) => sourceName(source, t));
  return (
    <Tooltip content={<TooltipContent title={title} lines={names} />}>
      <span
        className={styles.pill}
        role="img"
        aria-label={t('logistics.source.pillLabel', { title, tasks: names.join(t('common.listSeparator')) })}
      >
        <GanttIcon size={11} />
        {withCount ? <span className={styles.pillCount}>{t('logistics.source.taskCount', { count: sources.length })}</span> : null}
      </span>
    </Tooltip>
  );
}

interface IDeliveryLineSourcesProps {
  siteId: string;
  sources: IDeliveryLineSource[];
}

/**
 * The delivery drawer's "Raised from task" blocks — one per source, in the
 * order the tasks joined the order. Floor names come from the site's area
 * catalog (the Scheduler's own query); until it loads the booked row simply
 * names no floors.
 */
export function DeliverySources({ siteId, sources }: IDeliveryLineSourcesProps) {
  const catalog = useSiteLocations(siteId);
  const floorNames = new Map(catalog.floors.map((floor) => [floor.id, floor.name]));
  return (
    <>
      {sources.map((source) => (
        <SourceBlock
          key={`${source.scheduleNumber}/${source.taskId}`}
          siteId={siteId}
          source={source}
          floorNames={floorNames}
        />
      ))}
    </>
  );
}

interface ISourceBlockProps {
  siteId: string;
  source: IDeliveryLineSource;
  floorNames: ReadonlyMap<string, string>;
}

function SourceBlock({ siteId, source, floorNames }: ISourceBlockProps) {
  const { t } = useTranslation();
  const { task } = source;
  return (
    <section className={styles.block} data-testid="delivery-source">
      <header className={styles.head}>
        <GanttIcon size={13} />
        <span>{t('logistics.source.heading')}</span>
        {/* A new tab: the reader keeps this order open while looking at the
            task, the mirror of the Stock tab's link to its order. */}
        {task ? (
          <button
            type="button"
            className={styles.open}
            onClick={() =>
              window.open(
                schedulerTaskPath(siteId, source.scheduleNumber, source.taskId, 'stock'),
                '_blank',
                'noopener,noreferrer',
              )
            }
          >
            {t('logistics.source.openTask')} <span aria-hidden="true">↗</span>
          </button>
        ) : null}
      </header>
      {task ? (
        <SourceTask task={task} floorNames={floorNames} />
      ) : (
        <p className={styles.removed}>{t('logistics.source.removed')}</p>
      )}
    </section>
  );
}

interface ISourceTaskProps {
  task: IDeliverySourceTask;
  floorNames: ReadonlyMap<string, string>;
}

/* The task's SHARE of the line (`IDeliverySource.quantity`) is deliberately
   not shown here (Niv, 2026-09-15: a second box under Booked "looks bad"). It
   is the Stock tab's number — the difference between it and the booking is
   what the "Add … to #ref" offer is about — and the block stays the task's
   live booking alone. */
function SourceTask({ task, floorNames }: ISourceTaskProps) {
  const { t, i18n } = useTranslation();
  // A schedule task can be undated: the plan stores '' for a missing date, and
  // formatting '' throws. A missing end shows as a dash; with neither date
  // there is no span to show.
  const day = (value: string) => (value === '' ? t('logistics.detail.empty') : formatDay(value, i18n.language, true));
  const dated = task.start !== '' || task.end !== '';
  return (
    <div className={styles.body}>
      <span className={styles.name}>{task.name}</span>
      <span className={styles.path}>{task.path.join(' › ')}</span>
      <span className={styles.facts}>
        {dated ? (
          <span>
            {day(task.start)} → {day(task.end)}
          </span>
        ) : null}
        {/* The Scheduler's own status pill and colours, so a task's status
            reads the same here as on its bar and in its drawer. The plain
            wrapper takes the facts row's drawn " · ", keeping it outside the
            pill's fill. */}
        <span>
          <TaskStatusPill status={task.status} />
        </span>
        <span>{task.progressPercent}%</span>
      </span>
      {task.booked ? <BookedRow booked={task.booked} floorNames={floorNames} /> : null}
    </div>
  );
}

/** "Booked · 1 × Slab · 2 × Wall | 3F, 4F" with the claim's amount at the right:
 *  what the task holds of this order's material. Types and floors are two
 *  groups with a rule between them — joined by the same " · " they read as one
 *  list. A long booking is cut to one line (`bookedSummary`) and the hint
 *  tooltip on the row lists all of it. */
function BookedRow({ booked, floorNames }: { booked: IDeliverySourceBooked; floorNames: ReadonlyMap<string, string> }) {
  const { t } = useTranslation();
  const summary = bookedSummary(booked, floorNames, t);
  const content = summary.full ? <TooltipContent title={summary.full.title} lines={summary.full.lines} divided /> : '';
  return (
    <div className={styles.booked} data-testid="source-booked">
      {/* An empty `content` renders the row bare: a short list has nothing to reveal. */}
      <Tooltip content={content}>
        <span className={styles.bookedWhat}>
          <span className={styles.bookedKinds} data-testid="booked-kinds">
            {summary.kinds}
            {summary.moreKinds !== '' ? (
              <>
                {' · '}
                <span className={styles.more}>{summary.moreKinds}</span>
              </>
            ) : null}
          </span>
          {summary.floors !== '' ? (
            <span
              className={summary.floorsCounted ? `${styles.bookedFloors} ${styles.more}` : styles.bookedFloors}
              data-testid="booked-floors"
            >
              {summary.floors}
            </span>
          ) : null}
        </span>
      </Tooltip>
      <b>
        {booked.quantity} {unitLabel(t, booked.unit)}
      </b>
    </div>
  );
}
